import React, { useState, useEffect, useContext, useCallback, useMemo } from "react";
import "./Style.css";
import MenuBar from "./MenuBar";
import { Button } from "@mui/material";
import { Warning as WarningIcon } from "@mui/icons-material";
import Footer from "./Footer";
import { useNavigate, useLocation } from "react-router-dom";
import { NotificationContext } from "../redux/NotificationProvider";
import DeleteModal from "./DeleteModal";
import Modal from "./Modal";
import { secondsToHms, hmsToSeconds } from "../helper-functions/helpers";
import PageTabs from "./shared/PageTabs";
import CollapsiblePanel from "./shared/CollapsiblePanel";
const envVariable = require("../EnvironmentVariables.json");

const Tabs = {
  BOOKING_REQUESTS: "Booking Requests",
  OPEN: "Open",
  IN_PROGRESS: "In-Progress",
  COMPLETE: "Completed",
};

const ROADMAP_TABS = [
  { id: "insights", label: "Insights", enabled: false, comingSoon: true },
  { id: "automation", label: "Automation", enabled: false, comingSoon: true },
  { id: "integrations", label: "Integrations", enabled: false, comingSoon: true },
];

const normalizeVehicleText = (value) => (value ? value.toString().trim() : "");

const stripLeadingYear = (value) =>
  normalizeVehicleText(value).replace(/^\d{4}\s+/, "");

const getVehicleDetails = (task = {}) => {
  const savedMake = normalizeVehicleText(task.make || task.car_make);
  const savedModel = normalizeVehicleText(task.model || task.car_model_name);
  const combinedVehicle = normalizeVehicleText(task.car_model);

  if (savedMake && savedModel) {
    return {
      make: savedMake,
      model: savedModel,
      combined: combinedVehicle || `${savedMake} ${savedModel}`.trim(),
    };
  }

  const withoutYear = stripLeadingYear(combinedVehicle);

  if (!withoutYear) {
    return {
      make: savedMake,
      model: savedModel,
      combined: combinedVehicle,
    };
  }

  if (savedMake && !savedModel) {
    const modelFromCombined = withoutYear
      .replace(new RegExp(`^${savedMake}\\s+`, "i"), "")
      .trim();

    return {
      make: savedMake,
      model: modelFromCombined || withoutYear,
      combined: combinedVehicle || `${savedMake} ${modelFromCombined}`.trim(),
    };
  }

  const parts = withoutYear.split(/\s+/).filter(Boolean);
  const parsedMake = savedMake || parts[0] || "";
  const parsedModel = savedModel || parts.slice(savedMake ? 0 : 1).join(" ");

  return {
    make: parsedMake,
    model: parsedModel,
    combined: combinedVehicle || `${parsedMake} ${parsedModel}`.trim(),
  };
};

const Tasks = () => {
  const location = useLocation();
  const itemsPerPage = 6;
  const [currentPage, setCurrentPage] = useState(1);
  const [tasks, setTasks] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const navigate = useNavigate();
  // Check if navigated from booking-requests route, otherwise load from localStorage
  const [currentTab, setCurrentTab] = useState(() => {
    if (location.pathname === "/booking-requests") {
      return Tabs.BOOKING_REQUESTS;
    }
    const savedTab = localStorage.getItem("tasksLastActiveTab");
    return savedTab && Object.values(Tabs).includes(savedTab) ? savedTab : Tabs.IN_PROGRESS;
  });
  const [searchByCustomer, setSearchByCustomer] = useState("");
  const [searchByVIN, setSearchByVIN] = useState("");
  const [searchByCarModel, setSearchByCarModel] = useState("");
  const [searchByCarMake, setSearchByCarMake] = useState("");
  const notify = useContext(NotificationContext);
  const [sortField, setSortField] = useState("task_id");
  const [sortOrder, setSortOrder] = useState("desc");
  const [mechanicFilter, setMechanicFilter] = useState("all");
  const [newTask, setNewTask] = useState({
    customerName: "",
    carModel: "",
    issueDescription: "",
    notes: "",
    vinNumber: "",
    phoneNumber: "",
  });

  // Booking Requests specific states
  const [pendingTasks, setPendingTasks] = useState([]);
  const [declineModalOpen, setDeclineModalOpen] = useState(false);
  const [acceptModalOpen, setAcceptModalOpen] = useState(false);
  const [taskToDecline, setTaskToDecline] = useState(null);
  const [editBookingModalOpen, setEditBookingModalOpen] = useState(false);
  const [bookingToEdit, setBookingToEdit] = useState(null);
  const [newAppointmentTime, setNewAppointmentTime] = useState("");

  const pageTabs = useMemo(
    () => [
      { id: Tabs.BOOKING_REQUESTS, label: Tabs.BOOKING_REQUESTS, enabled: true },
      { id: Tabs.OPEN, label: "Open Tasks", enabled: true },
      { id: Tabs.IN_PROGRESS, label: "In-Progress Tasks", enabled: true },
      { id: Tabs.COMPLETE, label: "Completed Tasks", enabled: true },
      ...ROADMAP_TABS,
    ],
    []
  );

  //Car History state
  const [carHistoryOpen, setCarHistoryOpen] = useState(false);
  const [selectedCarHistory, setSelectedCarHistory] = useState({ userCarId: null, carName: '' });
  const [carHistoryTasks, setCarHistoryTasks] = useState([]);
  const [carHistoryLoading, setCarHistoryLoading] = useState(false);

  const mechanicOptions = useMemo(() => {
    const mechanics = tasks
      .map((task) => task.Mechanic)
      .filter((mechanic) => mechanic && mechanic.trim() !== "");
    return Array.from(new Set(mechanics)).sort((a, b) => a.localeCompare(b));
  }, [tasks]);

  const fetchTasks = async (status) => {
    const SC_Id = localStorage.getItem("SC_Id");

    const url = new URL(envVariable.serverId + "/SCO/GetTasksByStatus.php");
    url.searchParams.append("SC_Id", SC_Id);
    url.searchParams.append("status", status);

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        throw new Error(`Network response was not ok: ${response.statusText}`);
      }

      const data = await response.json();
      if (data.status === "Error") {
        console.error(data.message);
        setTasks([]);
        setFilteredTasks([]);
      } else if (data.status === "Success" && Array.isArray(data.tasks)) {
        const fetchedTasks = [...data.tasks];
        setTasks(fetchedTasks);
        setCurrentPage(1);
      } else {
        setTasks([]);
        setFilteredTasks([]);
        throw new Error("Unexpected response structure");
      }
    } catch (error) {
      console.error("Failed to fetch tasks:", error);
    }
  };

  const fetchPendingTasks = async () => {
    const SC_Id = localStorage.getItem("SC_Id");
    const status = "Pending";

    try {
      const response = await fetch(
        envVariable.serverId + `/SCO/GetTasksByStatus.php?SC_Id=${SC_Id}&status=${status}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) throw new Error("Network response was not ok");

      const data = await response.json();
      setPendingTasks(data.tasks || []);
      setCurrentPage(1);
    } catch (error) {
      console.error("Error fetching pending tasks:", error);
      setPendingTasks([]);
    }
  };

  const fetchCarHistory = async (userCarId, carName) => {
    setSelectedCarHistory({ userCarId, carName });
    setCarHistoryOpen(true);
    setCarHistoryLoading(true);
    setCarHistoryTasks([]);
    try {
        const response = await fetch(envVariable.serverId + "/SCO/getCarHistory.php", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ user_car_id: userCarId }),
        });
        const data = await response.json();
        if (data.type === "success") {
            setCarHistoryTasks(data.results);
            console.log(data.results)
        }
    } catch (error) {
        console.error("Error fetching car history:", error);
    } finally {
        setCarHistoryLoading(false);
    }
  };


  const handleAcceptBooking = async (taskId, appointmentStart) => {
    try {
      let appointmentEnd = hmsToSeconds(appointmentStart.substring(11, 16));
      appointmentEnd = appointmentEnd + 1800; // add 30 min
      appointmentEnd = appointmentStart.substring(0, 11) + secondsToHms(appointmentEnd);

      const sc_id = localStorage.getItem("SC_Id");

      const res = await fetch(envVariable.serverId + `/mechanicSlots/checkAvailabilities.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          start: appointmentStart,
          end: appointmentEnd,
          sc_id: sc_id,
        }),
      });

      const resJson = await res.json();

      if (resJson.message.length === 0) {
        setAcceptModalOpen(true);
        return;
      }

      const mech_id = resJson.message[0].mechanic_id;

      await fetch(envVariable.serverId + `/mechanicSlots/makeSlotsUnavailable.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          task_id: taskId,
          sc_id: sc_id,
          calendar_start: appointmentStart,
          calendar_end: appointmentEnd,
          mechanic_id: mech_id,
        }),
      });

      await fetch(envVariable.serverId + `/mechanicSlots/updateTaskSlot.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          task_id: taskId,
          sc_id: sc_id,
          calendar_start: appointmentStart,
          calendar_end: appointmentEnd,
          mechanic_id: mech_id,
          status: "Open",
        }),
      });

      notify("Booking request accepted successfully", "success");
      fetchPendingTasks();
    } catch (error) {
      console.error("Error accepting booking request:", error);
      notify("Error accepting booking request", "error");
    }
  };

  const handleDeclineBooking = async () => {
    if (taskToDecline) {
      try {
        const response = await fetch(envVariable.serverId + `/SCO/deleteTask.php`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            task_id: taskToDecline,
          }),
        });

        const result = await response.json();

        if (result.type === 'success') {
          notify("Booking request declined successfully", "success");
          fetchPendingTasks();
          setDeclineModalOpen(false);
          setTaskToDecline(null);
        } else {
          throw new Error(result.message || "Failed to decline booking request");
        }
      } catch (error) {
        console.error("Error declining booking request:", error);
        notify("Error declining booking request", "error");
      }
    }
  };

  const handleEditBookingClick = (task) => {
    setBookingToEdit(task);
    // Format the datetime for input field (YYYY-MM-DDTHH:MM)
    const formattedTime = task.appointment_time ? task.appointment_time.substring(0, 16) : "";
    setNewAppointmentTime(formattedTime);
    setEditBookingModalOpen(true);
  };

  const handleSaveBookingEdit = async () => {
    if (!bookingToEdit || !newAppointmentTime) {
      notify("Please select a valid appointment time", "error");
      return;
    }

    try {
      const response = await fetch(envVariable.serverId + `/SCO/updateBookingAppointment.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          task_id: bookingToEdit.task_id,
          appointment_time: newAppointmentTime + ":00", // Add seconds
        }),
      });

      const data = await response.json();

      if (data.status === "Success") {
        notify("Appointment time updated successfully", "success");
        setEditBookingModalOpen(false);
        setBookingToEdit(null);
        setNewAppointmentTime("");
        fetchPendingTasks();
      } else {
        throw new Error(data.message || "Failed to update appointment time");
      }
    } catch (error) {
      console.error("Error updating appointment time:", error);
      notify("Error updating appointment time", "error");
    }
  };

  const handleBookingChat = (task) => {
    const formattedDate = formatDateSafe(task.created_at || task.appointment_time);
    navigate(`/tasks/chat/${task.task_id}`, {
      state: {
        customerId: task.Customer_id,
        customerName: task.Customer,
        mechanicId: task.mechanic_id,
        mechanicName: task.Mechanic,
        originDate: formattedDate,
        vin_number: task.vin_number,
        carId: task.user_car_id,
      },
    });
  };

  // Helper function to safely format dates
  const formatDateSafe = (dateValue) => {
    if (!dateValue) {
      // If no date provided, use today's date as fallback
      return new Date().toISOString().split("T")[0];
    }

    const date = new Date(dateValue);

    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.warn('[Tasks] Invalid date value:', dateValue, '- using today as fallback');
      return new Date().toISOString().split("T")[0];
    }

    return date.toISOString().split("T")[0];
  };

  // Parse datetime string from backend as UTC (backend stores in UTC)
  const parseUTCDateTime = (dateString) => {
    if (!dateString) return null;
    // Backend returns format: "YYYY-MM-DD HH:MM:SS"
    // Replace space with T and add Z to indicate UTC
    const isoString = dateString.replace(' ', 'T') + (dateString.includes('Z') ? '' : 'Z');
    const date = new Date(isoString);
    return isNaN(date.getTime()) ? null : date;
  };

  // Format datetime for display with space instead of T, converted to local timezone
  const formatLocalDateTimeDisplay = (date) => {
    if (!date) return '';
    const d = date instanceof Date ? date : parseUTCDateTime(date);
    if (!d || isNaN(d.getTime())) return '';

    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');

    return `${year}-${month}-${day} ${hours}:${minutes}`;
  };

  const navigateToChat = (task) => {
    const formattedDate = formatDateSafe(task.appointment_time || task.createdDate);
    console.log('[Tasks] Navigating to chat with date:', formattedDate);
    navigate(`/tasks/chat/${task.task_id}`, {
      state: {
        customerId: task.Customer_id,
        customerName: task.Customer,
        mechanicId: task.mechanic_id,
        mechanicName: task.Mechanic,
        originDate: formattedDate,
        vin_number: task.vin_number,
        carId: task.user_car_id,
      },
    });
  };

  const navigateToInvoiceHistory = (task) => {
    const formattedDate = formatDateSafe(task.appointment_time);
    navigate(`/tasks/invoice-history/${task.task_id}`, {
      state: {
        customerId: task.Customer_id,
        customerName: task.Customer,
        originDate: formattedDate,
        vin_number: task.vin_number,
        time: task.accumulated_secs,
        carId: task.user_car_id,
      },
    });
  };


  const handleDelete = async () => {
    if (!taskToDelete) return;

    try {
      const user_id = localStorage.getItem("User_Id");
      const sc_id = localStorage.getItem("SC_Id");

      const response = await fetch(envVariable.serverId + "/SCO/deleteTask.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          task_id: taskToDelete,
          deleted_by: user_id,
          sc_id: sc_id,
        }),
      });

      const result = await response.json();

      if (result.type === "success") {
        notify("Task archived successfully", "success");
        setDeleteModalOpen(false);
        setTaskToDelete(null);
        // Refresh the current tab
        if (currentTab === Tabs.BOOKING_REQUESTS) {
          fetchPendingTasks();
        } else {
          fetchTasks(currentTab);
        }
      } else {
        throw new Error(result.message || "Failed to delete task");
      }
    } catch (error) {
      console.error("Error deleting task:", error);
      notify("Error deleting task: " + error.message, "error");
    }
  };

  const handleTabClick = (tab) => {
    setCurrentPage(1);
    setCurrentTab(tab);
    // Save the current tab to localStorage
    localStorage.setItem('tasksLastActiveTab', tab);
  };

  const handleEdit = (task) => {
    const formattedDate = formatDateSafe(task.appointment_time);
    navigate(`/tasks/edit-task/${task.task_id}`, {
      state: {
        customerId: task.Customer_id,
        customerName: task.Customer,
        mechanicId: task.mechanic_id,
        mechanicName: task.Mechanic,
        originDate: formattedDate,
        vin_number: task.vin_number,
        carId: task.user_car_id,
      },
    });
  };
  const handleTaskSubmit = async (e) => {
    e.preventDefault();
    const phoneNumber = newTask.phoneNumber;
    if (phoneNumber.length !== 10) {
      notify("Phone number must be 10 digits.","error");
      return; // Prevent form submission
    }
    try {
      // get sc_id from local storage
      const SC_Id = localStorage.getItem("SC_Id");

      console.log("Sending data with SC_Id:", SC_Id);

      const taskPayload = {
        ...newTask,
        SC_Id,
        status: "Open",
      };

      const response = await fetch(envVariable.serverId + "/SCO/AddTask.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(taskPayload),
      });

      let data;
      try {
        data = await response.json();
      } catch (err) {
        data = { status: "Success" };
      }

      if (!response.ok || data.status !== "Success") {
        throw new Error("Failed to create task");
      }
      const message = `Hi ${newTask.customerName}, your task has been created successfully!`;
      sendSMSNotification(newTask.phoneNumber, message);
      // Clear the form, close modal, and refetch tasks
      setNewTask({
        customerName: "",
        phoneNumber: "",
        carModel: "",
        issueDescription: "",
        notes: "",
        vinNumber: "",
      });
      setModalOpen(false);
      fetchTasks(currentTab); // Refetch tasks to reflect the newly added task
    } catch (error) {
      console.log("Error creating task:", error);
      setModalOpen(false);
    }
  };
  // Clear the form and close modal

  const totalPages = Math.max(1, Math.ceil(filteredTasks.length / itemsPerPage));

  const handleLeftPagination = () => {
    setCurrentPage((prev) => Math.max(1, prev - 1));
  };

  const handleRightPagination = () => {
    setCurrentPage((prev) => Math.min(totalPages, prev + 1));
  };

  const handleCustomer = (customerName) => {
    setSearchByCustomer(customerName);
    setCurrentPage(1);
  };

  const handleVin = (vin_number) => {
    setSearchByVIN(vin_number);
    setCurrentPage(1);
  };

  const handleCarModelSearch = (value) => {
    setSearchByCarModel(value);
    setCurrentPage(1);
  };

  const handleCarMakeSearch = (value) => {
    setSearchByCarMake(value);
    setCurrentPage(1);
  };

  const handleSortFieldChange = (field) => {
    if (sortField === field) {
      // If clicking the same column, toggle the order
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      // If clicking a new column, set it and default to ascending
      setSortField(field);
      setSortOrder("asc");
    }
    setCurrentPage(1);
  };

  const handleMechanicFilterChange = (value) => {
    setMechanicFilter(value);
    setCurrentPage(1);
  };

  const getFilteredTasks = useCallback(
    (
      customerName = searchByCustomer,
      vin_number = searchByVIN,
      carModel = searchByCarModel,
      carMake = searchByCarMake,
      mechanic = mechanicFilter,
      sortKey = sortField,
      order = sortOrder
    ) => {
      const customerQuery = (customerName || "").toLowerCase();
      const vinQuery = (vin_number || "").toLowerCase();
      const carModelQuery = (carModel || "").toLowerCase();
      const carMakeQuery = (carMake || "").toLowerCase();

      const normalizeString = (value) => (value ? value.toString().toLowerCase() : "");

      const parseDate = (value) => {
        if (!value) {
          return null;
        }
        const dateValue = new Date(value);
        return Number.isNaN(dateValue.getTime()) ? null : dateValue.getTime();
      };

      const getSortValue = (task) => {
        switch (sortKey) {
          case "appointment_time":
            return parseDate(task.appointment_time);
          case "customer":
            return normalizeString(task.Customer);
          case "mechanic":
            return normalizeString(task.Mechanic);
          case "task_id":
          default:
            return Number(task.task_id) || 0;
        }
      };

      const sortedTasks = tasks
        .filter((task) => normalizeString(task.Customer).includes(customerQuery))
        .filter((task) => {
          if (!vinQuery) {
            return true;
          }
          const vin = normalizeString(task.vin_number);
          return vin.includes(vinQuery);
        })
        .filter((task) => {
          if (!carModelQuery) {
            return true;
          }
          const vehicle = getVehicleDetails(task);
          return (
            normalizeString(vehicle.model).includes(carModelQuery) ||
            normalizeString(vehicle.combined).includes(carModelQuery)
          );
        })
        .filter((task) => {
          if (!carMakeQuery) {
            return true;
          }
          const vehicle = getVehicleDetails(task);
          return (
            normalizeString(vehicle.make).includes(carMakeQuery) ||
            normalizeString(vehicle.combined).includes(carMakeQuery)
          );
        })
        .filter((task) => {
          if (mechanic === "all") {
            return true;
          }
          return task.Mechanic === mechanic;
        })
        .sort((a, b) => {
          const aValue = getSortValue(a);
          const bValue = getSortValue(b);

          if (aValue === bValue) {
            return 0;
          }

          if (aValue === null || aValue === undefined || aValue === "") {
            return 1;
          }

          if (bValue === null || bValue === undefined || bValue === "") {
            return -1;
          }

          let comparison = 0;

          if (typeof aValue === "string" && typeof bValue === "string") {
            comparison = aValue.localeCompare(bValue);
          } else {
            comparison = aValue > bValue ? 1 : -1;
          }

          return order === "asc" ? comparison : -comparison;
        });

      setFilteredTasks(sortedTasks);
    },
    [tasks, searchByCustomer, searchByVIN, searchByCarModel, searchByCarMake, mechanicFilter, sortField, sortOrder]
  );
  useEffect(() => {
    if (currentTab === Tabs.BOOKING_REQUESTS) {
      fetchPendingTasks();
    } else {
      fetchTasks(currentTab);
    }
  }, [currentTab, location.key]); // Refetch when navigating back from edit page

  useEffect(() => {
    getFilteredTasks();
  }, [getFilteredTasks]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(filteredTasks.length / itemsPerPage));
    if (currentPage > maxPage) {
      setCurrentPage(maxPage);
    }
  }, [filteredTasks.length, currentPage, itemsPerPage]);
  const renderActionButtons = (task) => {
    return (
      <>
        <button className="edit-view-cls" onClick={() => handleEdit(task)}>
          Edit / View
        </button>
        <button className="chat-cls" onClick={() => navigateToChat(task)}>
          Chat
        </button>
        {currentTab === Tabs.COMPLETE && (
          // <button className="invoice-cls" onClick={() => navigateToInvoice(task)}>
          <button className="invoice-cls" onClick={() => navigateToInvoiceHistory(task)}>
            View Invoice
          </button>
        )}
        <button
          className="action-button decline"
          onClick={() => {
            setTaskToDelete(task.task_id);
            setDeleteModalOpen(true);
          }}
        >
          Delete
        </button>
      </>
    );
  };
  const sendSMSNotification = async (phoneNumber, message) => {
    try {
      // Sending data via POST request to your PHP endpoint
      const response = await fetch(envVariable.serverId + "/SCO/sendSMS.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          phoneNumber: phoneNumber,
          message: message,
        }),
      });

      const data = await response.json(); // Assuming your PHP returns a JSON response

      if (data.status === "Success") {
        console.log("SMS sent successfully!");
      } else {
        console.error("Failed to send SMS:", data.message);
      }
    } catch (error) {
      console.error("Error in sending SMS:", error);
    }
  };

  return (
    <>
      <MenuBar />

      {/* Modals for Booking Requests */}
      <DeleteModal
        isOpen={declineModalOpen}
        onClose={() => setDeclineModalOpen(false)}
        onConfirm={handleDeclineBooking}
      >
        <h2>Decline Booking Request</h2>
        <p>Are you sure you want to decline this booking request?</p>
      </DeleteModal>

      {/* Delete Task Modal */}
      <DeleteModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDelete}
      >
        <h2>Delete Task</h2>
        <p>Are you sure you want to delete this task?</p>
        <p style={{ fontSize: "14px", color: "#666" }}>
          The task will be moved to Deleted Tasks and can be restored later.
        </p>
      </DeleteModal>

      <Modal
        isOpen={acceptModalOpen}
        title={"Accept Booking Error"}
        dialog={"No available mechanics for this time slot"}
        showButtons={false}
        onClose={() => setAcceptModalOpen(false)}
      />

      {/* Edit Booking Modal */}
      {editBookingModalOpen && (
        <div className="modal-overlay" style={{ zIndex: 9999 }}>
          <div className="modal-content" style={{
            maxWidth: "500px",
            minHeight: "480px",
            padding: "30px 30px 40px 30px",
            position: "relative",
            zIndex: 10000
          }}>
            <h2 style={{ marginBottom: "20px" }}>Edit Booking Request</h2>
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              padding: "12px",
              backgroundColor: "#fff3cd",
              border: "1px solid #ffc107",
              borderRadius: "4px",
              marginBottom: "25px"
            }}>
              <WarningIcon style={{ color: "#856404" }} />
              <span style={{ color: "#856404", fontSize: "14px" }}>
                <strong>Note:</strong> Confirm with customer before changing the appointment time
              </span>
            </div>

            <div style={{ marginBottom: "40px" }}>
              <label style={{ display: "block", marginBottom: "10px", fontWeight: "bold" }}>
                Customer: {bookingToEdit?.Customer}
              </label>
              <label style={{ display: "block", marginBottom: "10px", fontWeight: "bold" }}>
                Car Model: {bookingToEdit?.car_model}
              </label>
              <label style={{ display: "block", marginBottom: "10px", fontWeight: "bold" }}>
                New Appointment Time:
              </label>
              <input
                type="datetime-local"
                value={newAppointmentTime}
                onChange={(e) => setNewAppointmentTime(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px",
                  fontSize: "14px",
                  border: "1px solid #ccc",
                  borderRadius: "4px"
                }}
              />
            </div>

            <div style={{ display: "flex", gap: "10px", justifyContent: "center", marginTop: "auto", paddingTop: "20px" }}>
              <Button
                variant="contained"
                sx={{ backgroundColor: "grey.500", color: "white" }}
                onClick={() => {
                  setEditBookingModalOpen(false);
                  setBookingToEdit(null);
                  setNewAppointmentTime("");
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSaveBookingEdit}
              >
                Save Changes
              </Button>
            </div>
          </div>
        </div>
      )}

      <PageTabs
        tabs={pageTabs}
        currentTab={currentTab}
        onChange={handleTabClick}
        ariaLabel="Tasks status tabs"
      />

      <CollapsiblePanel
        id="tasks-toolbar"
        title={`${currentTab} controls`}
        className="sco-toolbar-collapsible"
      >
        <div className="tasks-search-section sco-toolbar">
          <div
            className="tasks-search-inputs"
            style={{ display: "flex", flexDirection: "column", gap: "12px" }}
          >
            <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
              <div className="page-input-block">
                <label className="task-label">Customer&apos;s Name</label>
                <input
                  type="text"
                  placeholder="Search by Customer's Name"
                  className="search-input"
                  value={searchByCustomer}
                  onChange={(e) => {
                    handleCustomer(e.target.value);
                  }}
                />
              </div>
              <div className="page-input-block">
                <label className="task-label">VIN Number</label>
                <input
                  type="text"
                  placeholder="Search by VIN Number"
                  className="search-input"
                  value={searchByVIN}
                  onChange={(e) => handleVin(e.target.value)}
                />
              </div>
            </div>
            <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
              <div className="page-input-block">
                <label className="task-label">Car Model</label>
                <input
                  type="text"
                  placeholder="Search by Model"
                  className="search-input"
                  value={searchByCarModel}
                  onChange={(e) => handleCarModelSearch(e.target.value)}
                />
              </div>
              <div className="page-input-block">
                <label className="task-label">Car Make</label>
                <input
                  type="text"
                  placeholder="Search by Make"
                  className="search-input"
                  value={searchByCarMake}
                  onChange={(e) => handleCarMakeSearch(e.target.value)}
                />
              </div>
            </div>
            {currentTab !== Tabs.BOOKING_REQUESTS && (
              <div className="task-control">
                <span className="task-control-label">Filter</span>
                <select
                  className="task-select"
                  value={mechanicFilter}
                  onChange={(e) => handleMechanicFilterChange(e.target.value)}
                >
                  <option value="all">All Mechanics</option>
                  {mechanicOptions.map((mechanic) => (
                    <option key={mechanic} value={mechanic}>
                      {mechanic}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
          <div className="sco-toolbar-actions">
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => navigate("/tasks/deleted")}
            >
              View Archived Tasks
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate("/tasks/create")}
            >
              Create A Task
            </Button>
          </div>
        </div>
      </CollapsiblePanel>

      {modalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Create a New Task</h2>
            <form onSubmit={handleTaskSubmit}>
              <input
                type="text"
                placeholder="Customer Name"
                value={newTask.customerName}
                onChange={(e) => setNewTask({ ...newTask, customerName: e.target.value })}
                required
              />
              <input
                type="tel"
                placeholder="Phone Number"
                value={newTask.phoneNumber}
                onChange={(e) => setNewTask({ ...newTask, phoneNumber: e.target.value })}
                required
              />
              <input
                type="text"
                placeholder="Car Model"
                value={newTask.carModel}
                onChange={(e) => setNewTask({ ...newTask, carModel: e.target.value })}
                required
              />
              <textarea
                placeholder="Issue Description"
                value={newTask.issueDescription}
                onChange={(e) => setNewTask({ ...newTask, issueDescription: e.target.value })}
                required
              ></textarea>
              <textarea
                placeholder="Notes (Optional)"
                value={newTask.notes}
                onChange={(e) => setNewTask({ ...newTask, notes: e.target.value })}
              />
              <input
                type="text"
                placeholder="VIN Number (Optional)"
                value={newTask.vinNumber}
                onChange={(e) => setNewTask({ ...newTask, vinNumber: e.target.value })}
              />
              <div className="modal-actions">
                <Button type="button" onClick={() => setModalOpen(false)} color="secondary">
                  Cancel
                </Button>
                <Button
  variant="contained"
  color="primary"
  onClick={() => navigate("/tasks/create")}
>
  Create A Task
</Button>

              </div>
            </form>
          </div>
        </div>
      )}

      {/* Booking Requests Table */}
      {currentTab === Tabs.BOOKING_REQUESTS && (
        <div className="tasks-container">
          {(() => {
            const filteredBookings = pendingTasks
              .filter((task) =>
                (task?.Customer || "").toLowerCase().includes(searchByCustomer.toLowerCase())
              )
              .filter((task) =>
                (task?.vin_number || "").toLowerCase().includes(searchByVIN.toLowerCase())
              )
              .filter((task) => {
                const vehicle = getVehicleDetails(task);
                return (
                  !searchByCarModel.trim() ||
                  vehicle.model.toLowerCase().includes(searchByCarModel.toLowerCase()) ||
                  vehicle.combined.toLowerCase().includes(searchByCarModel.toLowerCase())
                );
              })
              .filter((task) => {
                const vehicle = getVehicleDetails(task);
                return (
                  !searchByCarMake.trim() ||
                  vehicle.make.toLowerCase().includes(searchByCarMake.toLowerCase()) ||
                  vehicle.combined.toLowerCase().includes(searchByCarMake.toLowerCase())
                );
              });

            if (filteredBookings.length === 0) {
              return (
                <p style={{ textAlign: "center", fontSize: 24, marginTop: 30 }}>
                  No booking requests available
                </p>
              );
            }

            return (
              <>
                <table className="tasks-table booking-requests-table">
                  <thead>
                    <tr>
                      <th>Task ID</th>
                      <th>Customer</th>
                      <th>Car Model</th>
                      <th>Car Make</th>
                      <th>Car Name</th>
                      <th>Service Requirement</th>
                      <th>Appointment</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredBookings
                      .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                      .map((task) => {
                        const vehicle = getVehicleDetails(task);
                        return (
                        <tr key={task.task_id}>
                          <td>{task.task_id}</td>
                          <td>{task.Customer}</td>
                          <td>{vehicle.model || "N/A"}</td>
                          <td>{vehicle.make || "N/A"}</td>
                          <td>
                              {task.user_car_id ? (
                                  <button
                                      className="car-name-link"
                                      onClick={() => fetchCarHistory(task.user_car_id, task.car_name)}
                                  >
                                      {task.car_name || "View History"}
                                  </button>
                              ) : (
                                  <span style={{ color: "#999" }}>N/A</span>
                              )}
                          </td>
                          <td>{task.service_info}</td>
                          <td>{task.appointment_time ? formatLocalDateTimeDisplay(task.appointment_time) : "N/A"}</td>
                          <td>
                            <button
                              className="action-button accept"
                              onClick={() => handleAcceptBooking(task.task_id, task.appointment_time)}
                            >
                              Accept
                            </button>
                            <button
                              className="action-button edit"
                              onClick={() => handleEditBookingClick(task)}
                            >
                              Edit
                            </button>
                            <button
                              className="action-button decline"
                              onClick={() => {
                                setTaskToDecline(task.task_id);
                                setDeclineModalOpen(true);
                              }}
                            >
                              Decline
                            </button>
                            <button
                              className="action-button chat"
                              onClick={() => handleBookingChat(task)}
                            >
                              Chat
                            </button>
                          </td>
                        </tr>
                      )})}
                  </tbody>
                </table>

                {/* Pagination for Booking Requests */}
                {filteredBookings.length > itemsPerPage && (
                  <div className="pagination pagination-compact" style={{ justifyContent: "center", marginTop: "20px" }}>
                    <button
                      className="page-item"
                      disabled={currentPage === 1}
                      onClick={handleLeftPagination}
                    >
                      {"<"}
                    </button>
                    <span className="pagination-status">
                      Page {currentPage} / {Math.ceil(filteredBookings.length / itemsPerPage)}
                    </span>
                    <button
                      className="page-item"
                      disabled={currentPage >= Math.ceil(filteredBookings.length / itemsPerPage)}
                      onClick={handleRightPagination}
                    >
                      {">"}
                    </button>
                  </div>
                )}
              </>
            );
          })()}
        </div>
      )}

      {/* Regular Tasks Table */}
      {currentTab !== Tabs.BOOKING_REQUESTS && (
        <div className="tasks-container">
        <table className="tasks-table">
          <thead>
            <tr>
              <th onClick={() => handleSortFieldChange("task_id")} className="sortable-header">
                Task ID {sortField === "task_id" && (sortOrder === "asc" ? "↑" : "↓")}
              </th>
              <th onClick={() => handleSortFieldChange("mechanic")} className="sortable-header">
                Assigned to {sortField === "mechanic" && (sortOrder === "asc" ? "↑" : "↓")}
              </th>
              <th onClick={() => handleSortFieldChange("appointment_time")} className="sortable-header">
                Appointment {sortField === "appointment_time" && (sortOrder === "asc" ? "↑" : "↓")}
              </th>
              <th>Actual Start</th>
              <th onClick={() => handleSortFieldChange("customer")} className="sortable-header">
                Customer Name {sortField === "customer" && (sortOrder === "asc" ? "↑" : "↓")}
              </th>
              <th>Car Model</th>
              <th>Car Make</th>
              <th>Car Name</th>
              <th>Issue Description</th>
              <th>Status</th>
              <th>VIN Number</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredTasks.length !== 0 ? (
              filteredTasks
                .slice((currentPage - 1) * itemsPerPage, (currentPage - 1) * itemsPerPage + itemsPerPage)
                .map((task) => {
                  const vehicle = getVehicleDetails(task);
                  return (
                  <tr key={task.task_id}>
                    <td>{task.task_id}</td>
                    <td>{task.Mechanic}</td>
                    <td>{task.appointment_time ? formatLocalDateTimeDisplay(task.appointment_time) : "N/A"}</td>

                    <td>{task.calendar_start ? formatLocalDateTimeDisplay(task.calendar_start) : "N/A"}</td>
                    <td>{task.Customer}</td>
                    <td>{vehicle.model || "N/A"}</td>
                    <td>{vehicle.make || "N/A"}</td>
                    <td>
                      {task.user_car_id ? (
                          <button
                              className="car-name-link"
                              onClick={() => fetchCarHistory(task.user_car_id, task.car_name)}
                          >
                              {task.car_name || "View History"}
                          </button>
                      ) : (
                          <span style={{ color: "#999" }}>N/A</span>
                      )}
                  </td>
                    <td>{task.service_info}</td>
                    <td>{task.status}</td>
                    <td>{task.vin_number}</td>

                    <td>{renderActionButtons(task)}</td>
                  </tr>
                )})
            ) : (
              <></>
            )}
          </tbody>
        </table>

        {filteredTasks.length === 0 ? (
          <p style={{ textAlign: "center", fontSize: 24, marginTop: 30 }}> No tasks that fit description</p>
        ) : (
          <></>
        )}

        {/* Pagination at the bottom */}
        {filteredTasks.length > 0 && (
          <div className="pagination pagination-compact" style={{ justifyContent: "center", marginTop: "20px" }}>
            <button
              className="page-item"
              disabled={currentPage === 1}
              onClick={handleLeftPagination}
            >
              {"<"}
            </button>
            <span className="pagination-status">
              Page {currentPage} / {totalPages}
            </span>
            <button
              className="page-item"
              disabled={currentPage >= totalPages || filteredTasks.length === 0}
              onClick={handleRightPagination}
            >
              {">"}
            </button>
          </div>
        )}
        </div>
      )}

      {carHistoryOpen && (
        <div className="modal-overlay" style={{ zIndex: 9999 }} onClick={() => setCarHistoryOpen(false)}>
            <div
                className="modal-content"
                style={{ width: "fit-content",
                          minWidth: "500px",
                          maxWidth: "90vw",
                          minHeight: "700px",
                          //maxHeight: "80vh",
                          padding: "30px",
                          overflowY: "auto" }}
                onClick={(e) => e.stopPropagation()}
            >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px"}}>
                    <div>
                        <h2 style={{ margin: 0 }}>Appointment History</h2>
                        <p style={{ margin: "4px 0 0", color: "#1E40AF", fontSize: "20px", fontWeight: "bold", textDecorationLine: 'underline'}}>
                            {selectedCarHistory.carName}
                        </p>
                    </div>
                    <button
                        onClick={() => setCarHistoryOpen(false)}
                        style={{
                            background: "#F0F0F0", border: "none", borderRadius: "50%",
                            width: "32px", height: "32px", cursor: "pointer", fontSize: "16px"
                        }}
                    >
                        ✕
                    </button>
                </div>

                {carHistoryLoading ? (
                    <p style={{ textAlign: "center", padding: "40px 0" }}>Loading history...</p>
                ) : carHistoryTasks.length === 0 ? (
                    <p style={{ textAlign: "center", color: "#777", padding: "40px 0" }}>
                        No appointments found for this vehicle.
                    </p>
                ) : (
                    <table className="tasks-table" style={{ marginTop: 0 }}>
                        <thead>
                            <tr>
                                <th>Task ID</th>
                                <th>Service Centre</th>
                                <th>Service</th>
                                <th>Status</th>
                                <th>Appointment</th>
                                <th>Booked On</th>
                            </tr>
                        </thead>
                        <tbody>
                            {carHistoryTasks.map((task) => (
                                <tr key={task.task_id}>
                                    <td>{task.task_id}</td>
                                    <td>{task.sc_name ?? "—"}</td>
                                    <td>{task.service_info ?? "—"}</td>
                                    <td>
                                        <span style={{
                                            backgroundColor:
                                                task.status === "Completed" ? "#D1FAE5" :
                                                task.status === "In-Progress" ? "#DBEAFE" :
                                                task.status === "Pending" ? "#FEF3C7" : "#F3F4F6",
                                            color:
                                                task.status === "Completed" ? "#065F46" :
                                                task.status === "In-Progress" ? "#1E40AF" :
                                                task.status === "Pending" ? "#92400E" : "#374151",
                                            padding: "2px 10px", borderRadius: "12px", fontSize: "13px", fontWeight: 600
                                        }}>
                                            {task.status}
                                        </span>
                                    </td>
                                    <td>{task.appointment_time ? formatLocalDateTimeDisplay(task.appointment_time) : "N/A"}</td>
                                    <td>{task.created_at ? formatLocalDateTimeDisplay(task.created_at) : "N/A"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
      )}
      <Footer />
    </>
  );
};

export default Tasks;
